/**
 * Per-user toggle between the current and redesigned Manage Returns pages.
 * Saves manage_returns_new_enabled via ui-settings, then swaps the design with a client-side
 * route change - both designs are served by the same Laravel page.
 */
import { observer } from "mobx-react";
import { useNavigate, useParams } from "react-router-dom";
import { Toggle, LABEL_POSITIONS } from "../../../../components/inputs/toggle/toggle";
import useDashboardTranslation from "../../../../dashboard-translation/hooks/dashboard-translation-hook";
import dashboardTranslationsStore from "../../../../dashboard-translation/stores/dashboard-translations-store";
import uiSettingsStore from "../../../../stores/ui-settings-store";
import {
    MANAGE_RETURNS_DESIGN_VERSION,
    trackManageReturnsDesignToggle,
} from "../../utils/manage-returns-list-mixpanel";
import { buildManageReturnsDesignPath } from "../../utils/manage-returns-redesign-rollout";
import "./manage-returns-redesign-toggle.scss";

const TOGGLE_LABEL_KEY = "NEW_MANAGE_RETURNS_PAGE_TOGGLE";
const TOGGLE_LABEL_SCOPE = "dashboard-returns-page";
const TOGGLE_LABEL_FALLBACK = "Try new view";

const ManageReturnsRedesignToggle: React.FC = observer(() => {
    const { getTranslation } = useDashboardTranslation();
    const { rmaId } = useParams();
    const navigate = useNavigate();
    const scope = TOGGLE_LABEL_SCOPE;
    const disabled =
        uiSettingsStore.isLoading ||
        uiSettingsStore.isSaving() ||
        dashboardTranslationsStore.isLoading;

    const onChange = async (checked: boolean) => {
        trackManageReturnsDesignToggle(
            checked ? MANAGE_RETURNS_DESIGN_VERSION.V2 : MANAGE_RETURNS_DESIGN_VERSION.V1,
            checked ? MANAGE_RETURNS_DESIGN_VERSION.V1 : MANAGE_RETURNS_DESIGN_VERSION.V2,
        );
        const saved = await uiSettingsStore.saveManageReturnsNewEnabled(checked);
        if (saved) {
            navigate(buildManageReturnsDesignPath(checked, rmaId));
        }
    };

    const translatedLabel = getTranslation(TOGGLE_LABEL_KEY, scope);
    const label = translatedLabel === `${scope}.${TOGGLE_LABEL_KEY}`
        ? TOGGLE_LABEL_FALLBACK
        : translatedLabel;

    return (
        <div className="manage-returns-redesign-toggle">
            <Toggle
                disabled={disabled}
                checked={uiSettingsStore.manageReturnsNewEnabled}
                label={label}
                labelPosition={LABEL_POSITIONS.LEFT}
                onChange={onChange}
            />
        </div>
    );
});

export default ManageReturnsRedesignToggle;
